import type { ViewState } from "../../types/warehouse.types";
import { Size, RENDER_CONSTANTS, PlaceColumn, PlaceRendererConfig } from "../core/types";
import type { PlaceLayoutCache } from "./PlaceLayoutCache";

/**
 * Filters out columns outside viewport
 */
export class Culler {
  /**
   * Returns only columns visible in viewport
   */
  cull(
    columns: PlaceColumn[],
    viewState: ViewState,
    worldToScreen: (x: number, y: number) => { x: number; y: number },
    canvasSize: Size,
    layoutCache: PlaceLayoutCache,
    globalMaxLevel: number,
    config: PlaceRendererConfig
  ): PlaceColumn[] {
    const visible: PlaceColumn[] = [];

    for (const column of columns) {
      if (this.isColumnVisible(column, viewState, worldToScreen, canvasSize, layoutCache, globalMaxLevel, config)) {
        visible.push(column);
      }
    }

    return visible;
  }

  private isColumnVisible(
    column: PlaceColumn,
    viewState: ViewState,
    worldToScreen: (x: number, y: number) => { x: number; y: number },
    canvasSize: Size,
    layoutCache: PlaceLayoutCache,
    globalMaxLevel: number,
    config: PlaceRendererConfig
  ): boolean {
    const basePlace = column.places[1];
    if (!basePlace) return false;

    const pos = worldToScreen(column.baseX, column.baseY);

    const dims = layoutCache.getDimensions(basePlace, viewState, config);
    const { placeWidth, placeHeight, isoDepthX, isoDepthY, placeSpacing } = dims;

    const levelHeight = basePlace.placeDimensions.levelHeight * viewState.scale;
    const totalHeight = globalMaxLevel * (levelHeight + placeSpacing);

    const minX = Math.min(pos.x - placeWidth / 2, pos.x - placeWidth / 2 + isoDepthX);
    const maxX = Math.max(pos.x + placeWidth / 2, pos.x + placeWidth / 2 + isoDepthX);
    const minY = pos.y - totalHeight;
    const maxY = pos.y + placeHeight + Math.abs(isoDepthY);

    const buffer = RENDER_CONSTANTS.CULLING_BUFFER;

    return !(
      maxX < -buffer ||
      minX > canvasSize.width + buffer ||
      maxY < -buffer ||
      minY > canvasSize.height + buffer
    );
  }
}
